import React, { forwardRef, useImperativeHandle, useEffect, useLayoutEffect, useRef, useState } from "react";
import { textLines } from "./StudioCanvas";
import { getStyle, resolveText } from "./studioModel";

export default forwardRef(function StudioInlineText({ node, doc, content, zoom, onSave, onClose, onResize, onDraftChange, active = true }, ref) {
  const initial = useRef(resolveText(node, content));
  const [text, setText] = useState(initial.current);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const input = useRef(null), busy = useRef(null);
  const activeRef = useRef(active);
  activeRef.current = active;
  const style = getStyle(doc, node);
  const singleLinePrice = node.role === "price" && node.h < style.fontSize * style.lineHeight * 2;
  useEffect(() => { if (active) { input.current?.focus(); input.current?.select(); } }, [active]);
  useEffect(() => {
    if (status !== "saved") return;
    const timer = setTimeout(onClose, 1100);
    return () => clearTimeout(timer);
  }, [status, onClose]);
  useLayoutEffect(() => {
    if (!input.current) return;
    // Measure at document scale, just like SVG; scaling small font sizes first
    // changes browser rounding and can introduce an extra wrapped line.
    const lines = textLines(text, style, node.w).length;
    input.current.style.height = `${style.fontSize * Math.max(1.3, style.lineHeight * lines)}px`;
  }, [text, node.w, style]);
  const save = () => {
    if (busy.current) return busy.current;
    if (status === "saved") return Promise.resolve(true);
    if (text === initial.current) { onClose(); return Promise.resolve(true); }
    setStatus("saving"); setError("");
    busy.current = (async () => {
      try {
        await onSave(node, text);
        setStatus("saved");
        if (!node.binding) onClose();
        return true;
      } catch (err) {
        setStatus("");
        setError(err.response?.data?.message || err.message || "Could not save. Please retry.");
        if (activeRef.current) input.current?.focus();
        return false;
      } finally { busy.current = null; }
    })();
    return busy.current;
  };
  useImperativeHandle(ref, () => ({ save }));
  return (
    <div className={`ms-inline-text ${status === "saved" ? "is-saved" : ""}`}
      style={{ left: node.x * zoom, top: node.y * zoom, width: node.w * zoom,
        height: node.h * zoom, transform: `rotate(${node.rotation || 0}deg)` }}
      onPointerDown={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Escape") { e.preventDefault(); if (!busy.current) onClose(); }
        if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); save(); }
      }}>
      <textarea ref={input} aria-label={!active ? "Pending text edit" : node.binding ? "Edit linked CMS text" : "Edit page text"}
        title="Enter to save · Shift+Enter for a new line · Escape to cancel"
        wrap={singleLinePrice ? "off" : "soft"}
        value={text} maxLength={20000} readOnly={!active || status === "saving" || status === "saved"}
        onChange={(e) => { setText(e.target.value); onDraftChange?.(e.target.value); }}
        onBlur={(e) => { if (!e.currentTarget.parentElement.contains(e.relatedTarget)) save(); }}
        style={{ width: node.w, transform: `scale(${zoom})`, transformOrigin: "top left",
          padding: 0, border: 0, minHeight: 0,
          fontFamily: style.fontFamily, fontSize: style.fontSize,
          lineHeight: style.lineHeight, fontWeight: style.fontWeight, fontStyle: style.fontStyle,
          letterSpacing: style.letterSpacing || 0, textAlign: style.align,
          fontVariant: style.fontVariant, fontFeatureSettings: style.fontFeatureSettings,
          whiteSpace: singleLinePrice ? "pre" : "pre-wrap", overflowWrap: "normal", wordBreak: "normal",
          textTransform: style.textTransform === "titlecase" ? undefined : style.textTransform, color: style.fill, background: "transparent" }} />
      {onResize && ["nw", "ne", "sw", "se"].map((corner) => (
        <button key={corner} type="button" className={`ms-inline-resize ms-inline-resize-${corner}`}
          aria-label={`Resize text ${corner}`} disabled={status === "saving" || status === "saved"}
          onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); onResize(e, node, corner); }} />
      ))}
      <div className="ms-inline-text-status" role={error ? "alert" : "status"}>
        {error || (status === "saving" ? (node.binding ? "Saving to CMS…" : "Saving…") : status === "saved" ? "✓ CMS updated" : node.binding ? "Updates CMS" : "Page text")}
        {status !== "saved" && <span> · Enter to save</span>}
        {error && <>
          <button type="button" onPointerDown={(e) => e.preventDefault()} onClick={save}>Retry</button>
          <button type="button" onPointerDown={(e) => e.preventDefault()} onClick={onClose}>Cancel edit</button>
        </>}
      </div>
    </div>
  );
});
